import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { AuthContext } from '../context/AuthContext';
import { useRunTimeEnvironment, useScript } from '../utils/hooks';
import {
  emptyStepsPayload,
  formatKm,
  isPedometerNativeSupported,
  openHealthApp,
  requestStepPermission,
  requestTodaySteps,
  subscribeNativeSteps,
} from '../services/pedometerNative';

/** 만보기 씬이 보일 때 HC/HK 폴링 간격 */
export const PEDOMETER_POLL_MS = 5000;

export function pedometerTodayKey(apiDomain, accessToken) {
  return accessToken && apiDomain ? [`${apiDomain}/main/pedometer/today`, accessToken] : null;
}

/**
 * HC/HK 일일 합계만 표기·sync에 사용 (센서 추정 없음)
 *
 * @param {{ active?: boolean, onPollNativeSteps?: (payload: object) => void }} [options]
 */
export function usePedometerBridge({ active = false, onPollNativeSteps } = {}) {
  const RTE = useRunTimeEnvironment();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  // APP + 구버전: 네이티브 호출·폴링 전부 스킵
  const nativeSupported = RTE !== 'APP' || isPedometerNativeSupported(appInfo);
  const [native, setNative] = useState(emptyStepsPayload);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');
  const { data: today, mutate } = useSWR(pedometerTodayKey(API_SERVER_DOMAIN, accessToken), fetchWithToken);
  const lastSyncRef = useRef({ steps: -1, date: '', permission: '' });
  const syncInFlightRef = useRef(false);
  const healthHistoryOnceRef = useRef(false);
  const onPollNativeStepsRef = useRef(onPollNativeSteps);

  useEffect(() => {
    onPollNativeStepsRef.current = onPollNativeSteps;
  }, [onPollNativeSteps]);

  useScript(`${process.env.PUBLIC_URL}/flutter/flutter_script.js`);

  const applyNativePayload = useCallback(
    async (payload, { force = false, syncHealth = true } = {}) => {
      const base = payload || emptyStepsPayload();
      if (base.hasHealthStepHistory) {
        healthHistoryOnceRef.current = true;
      }
      const next = {
        ...base,
        hasHealthStepHistory: base.hasHealthStepHistory || healthHistoryOnceRef.current,
      };
      setNative(next);

      if (!syncHealth || !accessToken || !API_SERVER_DOMAIN) {
        return next;
      }

      const healthStep =
        next.permission === 'granted' ? Math.max(0, Number(next.healthStep ?? next.step) || 0) : 0;
      const last = lastSyncRef.current;
      const stepsIncreased = healthStep > last.steps;
      const dateChanged = next.date !== last.date;
      const permissionChanged = next.permission !== last.permission;
      // 권한 없으면 걸음 0으로 보상 sync 돌리지 않음 (permission 변경 시에만 통지)
      if (next.permission !== 'granted' && !force && !permissionChanged && last.steps >= 0) {
        return { ...next, syncStatus: 'skipped-no-permission' };
      }
      if (!force && last.steps >= 0 && !stepsIncreased && !dateChanged && !permissionChanged) {
        return { ...next, syncStatus: 'skipped-unchanged' };
      }

      if (syncInFlightRef.current) {
        return { ...next, syncStatus: 'skipped-busy' };
      }
      syncInFlightRef.current = true;
      try {
        const response = await fetch(`${API_SERVER_DOMAIN}/main/pedometer/sync`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            steps: healthStep,
            date: next.date,
            permission: next.permission,
            source: next.source,
          }),
        });
        const json = await response.json();
        if (json?.result === 'ok') {
          lastSyncRef.current = {
            steps: healthStep,
            date: next.date,
            permission: next.permission,
          };
          await mutate(json, { revalidate: false });
          return { ...next, syncStatus: 'ok', httpStatus: response.status };
        }
        return { ...next, syncStatus: 'api-fail', httpStatus: response.status };
      } catch (error) {
        console.error('pedometer sync failed', error);
        return { ...next, syncStatus: 'network-fail' };
      } finally {
        syncInFlightRef.current = false;
      }
    },
    [API_SERVER_DOMAIN, accessToken, mutate],
  );

  const refreshFromNative = useCallback(
    async (options) => {
      if (!nativeSupported) {
        return emptyStepsPayload();
      }
      const payload = await requestTodaySteps();
      return applyNativePayload(payload, { ...options, syncHealth: true });
    },
    [applyNativePayload, nativeSupported],
  );

  const requestPermission = useCallback(async () => {
    if (!nativeSupported) {
      return emptyStepsPayload();
    }
    const payload = await requestStepPermission();
    return applyNativePayload(payload, { force: true, syncHealth: true });
  }, [applyNativePayload, nativeSupported]);

  const openHealth = useCallback(async () => {
    if (!nativeSupported) return false;
    return openHealthApp();
  }, [nativeSupported]);

  useEffect(() => {
    if (RTE !== 'APP' || !today?.date || !window.flutter_inappwebview?.callHandler) {
      return undefined;
    }
    const claimedSteps = (today.segments ?? [])
      .filter((segment) => segment?.status === 'DONE')
      .map((segment) => segment.steps);
    window.flutter_inappwebview
      .callHandler('remember_step_rewards', today.date, claimedSteps)
      .catch(() => {});
    return undefined;
  }, [RTE, today]);

  useEffect(() => {
    const onVisibilityChange = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // 앱 진입: HC 1회 sync + resume 등 네이티브 push 구독 (지원 버전만)
  useEffect(() => {
    if (RTE !== 'APP' || !accessToken || !nativeSupported) {
      return undefined;
    }

    let cancelled = false;
    refreshFromNative({ force: true });
    const unsubscribe = subscribeNativeSteps((payload) => {
      if (cancelled) return;
      applyNativePayload(payload, { syncHealth: true });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [RTE, accessToken, nativeSupported, refreshFromNative, applyNativePayload]);

  // 만보기 씬 보일 때 HC/HK 폴링 (지원 버전만)
  useEffect(() => {
    if (RTE !== 'APP' || !accessToken || !nativeSupported || !active || !pageVisible) {
      return undefined;
    }

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const report = await refreshFromNative();
      if (!cancelled) {
        onPollNativeStepsRef.current?.(report);
      }
    };

    tick();
    const timerId = window.setInterval(tick, PEDOMETER_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timerId);
    };
  }, [RTE, accessToken, nativeSupported, active, pageVisible, refreshFromNative]);

  const granted = !nativeSupported || RTE !== 'APP' || native.permission === 'granted';
  const healthSteps = Number(native.healthStep ?? native.step) || 0;
  const displaySteps = granted ? healthSteps : 0;
  const displayKm = granted ? formatKm(displaySteps) : '0.00';
  const needsHealthAppGuide =
    nativeSupported &&
    RTE === 'APP' &&
    native.permission === 'granted' &&
    native.source === 'healthkit' &&
    !native.hasHealthStepHistory &&
    healthSteps === 0;

  return {
    native,
    today,
    mutate,
    displaySteps,
    displayKm,
    healthSteps,
    permission: nativeSupported ? native.permission : 'unavailable',
    source: native.source,
    hasHealthStepHistory: native.hasHealthStepHistory,
    needsHealthAppGuide,
    nativeSupported,
    requestPermission,
    openHealthApp: openHealth,
    refreshFromNative,
  };
}
